import { last } from 'lodash/fp';
import useSWR from 'swr';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import joinNonEmptyParams from '@/utils/joinNonEmptyParams';
import { isObjectLiteral } from '@/utils/objectHelpers';
import { fetcher } from '@/utils/services';
import laggy from '@/utils/swr/laggy';

/**
 * useDonationsSearch allows for you to conditionally call it by passing in a
 * first argument that can be falsey. For example, you have a state variable
 * that determines whether you should call it or not, pass that as the first
 * arg. If you always want to call it on every render, you can simply pass in
 * the params object as the first argument.
 *
 * Example of how to use with variadic arguments:
 *
 * useDonationsSearch(truthy | falsy, params)
 * useDonationsSearch(truthy | falsy, params, swrConfig)
 * useDonationsSearch(params)
 * useDonationsSearch(params, swrConfig)
 */
export default function useDonationsSearch(firstArg, ...rest) {
  let params = {};
  let swrConfig;
  const canFetch = !!firstArg;
  const lastArg = last(rest);

  if (isObjectLiteral(firstArg)) {
    params = firstArg;
  } else if (rest.length) {
    [params] = rest;
  }

  if (lastArg !== params) {
    swrConfig = lastArg;
  }

  const ownerOrgId = useOwnerOrgId();

  return useSWR(
    canFetch &&
      `donations/search?${joinNonEmptyParams({
        ownerOrgId,
        ...params,
      })}`,
    fetcher,
    { use: [laggy], ...swrConfig },
  );
}
